import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Plus, Play, Trash2, Loader2, BellRing, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("uk-UA").format(Math.round(n)));

const FUELS = [
  { v: "", l: "Будь-яке" },
  { v: "petrol", l: "Бензин" },
  { v: "diesel", l: "Дизель" },
  { v: "hybrid", l: "Гібрид" },
  { v: "electric", l: "Електро" },
];

function getSession() {
  let s = localStorage.getItem("wd_session");
  if (!s) { s = `wd-${Math.random().toString(36).slice(2, 14)}`; localStorage.setItem("wd_session", s); }
  return s;
}

export default function WatchDogPanel({ vehicle }) {
  const [sid] = useState(getSession);
  const [watches, setWatches] = useState([]);
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runInfo, setRunInfo] = useState(null);
  const [form, setForm] = useState({
    make: vehicle?.make || "Ford", model: vehicle?.model || "Mondeo",
    year_min: 2018, year_max: 2021, engine_cc_min: 1800, engine_cc_max: 2200,
    fuel: vehicle?.fuel || "diesel", max_price_uah: 750000,
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function refresh() {
    const [w, h] = await Promise.all([
      fetch(`${API}/watchdog/watches?session_id=${sid}`).then((r) => r.json()),
      fetch(`${API}/watchdog/hits?session_id=${sid}`).then((r) => r.json()),
    ]);
    setWatches(Array.isArray(w) ? w : []);
    setHits(Array.isArray(h) ? h : []);
  }
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

  async function addWatch() {
    setBusy(true);
    try {
      await fetch(`${API}/watchdog/watch`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid, name: `${form.make} ${form.model}`,
          criteria: {
            make: form.make, model: form.model,
            year_min: numOrNull(form.year_min), year_max: numOrNull(form.year_max),
            engine_cc_min: numOrNull(form.engine_cc_min), engine_cc_max: numOrNull(form.engine_cc_max),
            fuel: form.fuel || null, max_price_uah: numOrNull(form.max_price_uah),
          },
        }),
      });
      await refresh();
    } finally { setBusy(false); }
  }

  async function runAll() {
    setBusy(true); setRunInfo(null);
    try {
      const r = await fetch(`${API}/watchdog/run`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid }),
      }).then((x) => x.json());
      setRunInfo(r);
      await refresh();
    } finally { setBusy(false); }
  }

  async function del(id) {
    await fetch(`${API}/watchdog/watch/${id}?session_id=${sid}`, { method: "DELETE" });
    await refresh();
  }

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="watchdog-panel">
      <div className="mb-1 flex items-center gap-2">
        <Radar size={18} className="text-[#C73D3A]" />
        <h2 className="font-heading text-lg font-semibold">WatchDog — Софі стежить за ринком</h2>
      </div>
      <p className="mb-4 text-xs text-white/45">
        Збережіть критерії — Софі перевіряє ринок автоматично і показує нові та подешевілі пропозиції.
      </p>

      {/* create form */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <F label="Марка"><input data-testid="wd-make" className="inp" value={form.make} onChange={(e) => upd("make", e.target.value)} /></F>
        <F label="Модель"><input data-testid="wd-model" className="inp" value={form.model} onChange={(e) => upd("model", e.target.value)} /></F>
        <F label="Пальне">
          <select data-testid="wd-fuel" className="inp" value={form.fuel} onChange={(e) => upd("fuel", e.target.value)}>
            {FUELS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
          </select>
        </F>
        <F label="Макс. ціна, ₴"><input data-testid="wd-price" type="number" className="inp" value={form.max_price_uah} onChange={(e) => upd("max_price_uah", e.target.value)} /></F>
        <F label="Рік від"><input data-testid="wd-year-min" type="number" className="inp" value={form.year_min} onChange={(e) => upd("year_min", e.target.value)} /></F>
        <F label="Рік до"><input data-testid="wd-year-max" type="number" className="inp" value={form.year_max} onChange={(e) => upd("year_max", e.target.value)} /></F>
        <F label="Об'єм від, см³"><input data-testid="wd-cc-min" type="number" className="inp" value={form.engine_cc_min} onChange={(e) => upd("engine_cc_min", e.target.value)} /></F>
        <F label="Об'єм до, см³"><input data-testid="wd-cc-max" type="number" className="inp" value={form.engine_cc_max} onChange={(e) => upd("engine_cc_max", e.target.value)} /></F>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button data-testid="wd-add-btn" onClick={addWatch} disabled={busy}
          className="flex items-center gap-2 rounded-xl bg-[#C73D3A] px-5 py-2.5 font-heading font-semibold tracking-wide transition-colors hover:bg-[#d84a47] disabled:opacity-60">
          <Plus size={17} /> Додати стеження
        </button>
        {watches.length > 0 && (
          <button data-testid="wd-run-btn" onClick={runAll} disabled={busy}
            className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 font-medium transition-colors hover:border-[#C73D3A] disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Перевірити зараз
          </button>
        )}
      </div>

      {runInfo && (
        <div className="mt-3 text-sm text-white/60" data-testid="wd-run-info">
          Перевірено {runInfo.watches_checked} стежень · нових: <span className="text-[#22c55e]">{runInfo.new_hits}</span> · подешевшало: <span className="text-[#f59e0b]">{runInfo.cheaper_hits}</span>
        </div>
      )}

      {/* saved watches */}
      {watches.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="wd-watches">
          {watches.map((w) => (
            <div key={w.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid={`wd-watch-${w.id}`}>
              <div>
                <div className="font-heading font-semibold">{w.name}</div>
                <div className="text-xs text-white/45">
                  {w.criteria.year_min}–{w.criteria.year_max} · до {fmt(w.criteria.max_price_uah)} ₴ · знайдено {w.hits_count}
                </div>
              </div>
              <button data-testid={`wd-del-${w.id}`} onClick={() => del(w.id)}
                className="rounded-lg p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-[#C73D3A]">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* hits */}
      {hits.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white/80">
            <BellRing size={16} className="text-[#C73D3A]" /> Знахідки Софі ({hits.length})
          </div>
          <div className="grid gap-2 sm:grid-cols-2" data-testid="wd-hits">
            <AnimatePresence>
              {hits.map((h) => (
                <motion.div key={h.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`wd-hit-${h.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-white/90">{h.title}</div>
                    {h.hit_type === "cheaper"
                      ? <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#f59e0b]/15 px-2 py-0.5 text-xs text-[#f59e0b]"><TrendingDown size={12} /> дешевше</span>
                      : <span className="shrink-0 rounded-full bg-[#22c55e]/15 px-2 py-0.5 text-xs text-[#22c55e]">нове</span>}
                  </div>
                  <div className="mt-1 text-xs text-white/45">{h.year} · {fmt(h.mileage_km)} км · {h.engine_cc} см³</div>
                  <div className="mt-2 font-heading text-lg font-bold">{fmt(h.price_uah)} ₴</div>
                  {h.sofi_note && (
                    <div className="mt-2 flex gap-2 rounded-lg border border-[#C73D3A]/25 bg-[#C73D3A]/[0.06] p-2.5" data-testid={`wd-note-${h.id}`}>
                      <img src="/sofi-avatar.png" alt="Sofi" className="h-6 w-6 shrink-0 rounded-full object-cover ring-1 ring-[#C73D3A]/40" />
                      <p className="text-xs leading-relaxed text-white/75">{h.sofi_note}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block"><span className="mb-1 block text-xs text-white/50">{label}</span>{children}</label>
);
